import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { evaluationTests } from '@/lib/evaluationUtils';
import { Calendar, User, Clock, Bike, Star, Info, FileText } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const EvaluationDetailsModal = ({ isOpen, onClose, evaluation }) => {
    if (!evaluation) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl bg-[#1e293b] border-white/10 text-white max-h-[90vh] overflow-y-auto p-0 gap-0">
                {/* Header Estilo Banner */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-8">
                    <div className="flex justify-between items-start">
                        <div>
                            <h2 className="text-3xl font-bold text-white mb-1">Relatório de Avaliação</h2>
                            <p className="text-blue-100 opacity-80">Pedalando pela Inclusão – Processo de Evolução</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-md p-3 rounded-lg border border-white/20 text-right">
                            <p className="text-xs text-blue-100 uppercase font-semibold">Data da Avaliação</p>
                            <p className="text-xl font-bold">{new Date(evaluation.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</p>
                        </div>
                    </div>
                </div>

                <div className="p-8 space-y-8">
                    {/* Seção: Dados da Aula (Igual ao Formulário) */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-semibold flex items-center gap-2 text-blue-400">
                            <FileText className="h-5 w-5" />
                            Dados da Aula
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                                <p className="text-xs text-white/50 mb-1 flex items-center gap-1">
                                    <User className="h-3 w-3" /> Instrutor
                                </p>
                                <p className="font-medium text-white">{evaluation.instructor}</p>
                            </div>
                            <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                                <p className="text-xs text-white/50 mb-1 flex items-center gap-1">
                                    <Clock className="h-3 w-3" /> Duração
                                </p>
                                <p className="font-medium text-white">{evaluation.duration}</p>
                            </div>
                            <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                                <p className="text-xs text-white/50 mb-1 flex items-center gap-1">
                                    <Bike className="h-3 w-3" /> Aluno Pedala?
                                </p>
                                <p className="font-medium text-white capitalize">{evaluation.rides_bike}</p>
                            </div>
                        </div>
                    </div>

                    {/* Quadro de Pontuação - Estilo Registro */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-semibold flex items-center gap-2 text-blue-400">
                            <Star className="h-5 w-5" />
                            Quadro de Pontuação por Habilidade
                        </h4>
                        <div className="rounded-xl border border-white/10 overflow-hidden bg-white/5">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-white/10 border-b border-white/10 text-xs uppercase tracking-wider text-white/60">
                                        <th className="px-6 py-4 font-semibold">Habilidade Psicomotora</th>
                                        <th className="px-4 py-4 text-center font-semibold">0</th>
                                        <th className="px-4 py-4 text-center font-semibold">1</th>
                                        <th className="px-4 py-4 text-center font-semibold">2</th>
                                        <th className="px-4 py-4 text-center font-semibold">3</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {evaluationTests.map((test, index) => {
                                        const currentScore = evaluation.scores ? evaluation.scores[test.id] : 0;
                                        return (
                                            <tr key={test.id} className="hover:bg-white/5 transition-colors">
                                                <td className="px-6 py-4">
                                                    <p className="text-sm font-bold text-white">{index + 1}. {test.name}</p>
                                                    <p className="text-xs text-white/40 italic mt-0.5">{test.description}</p>
                                                </td>
                                                {[0, 1, 2, 3].map((scoreLevel) => (
                                                    <td key={scoreLevel} className="px-4 py-4 text-center">
                                                        <div
                                                            className={cn(
                                                                "w-4 h-4 rounded-full border-2 mx-auto flex items-center justify-center transition-all",
                                                                currentScore === scoreLevel
                                                                    ? "border-green-500 bg-green-500/20 shadow-[0_0_10px_rgba(34,197,94,0.3)]"
                                                                    : "border-white/10 bg-transparent"
                                                            )}
                                                        >
                                                            {currentScore === scoreLevel && (
                                                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                                            )}
                                                        </div>
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Detalhamento das Notas (O que foi removido e agora voltou) */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-semibold uppercase tracking-widest text-white/40">Detalhamento dos Resultados</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {evaluationTests.map((test) => {
                                const score = evaluation.scores ? evaluation.scores[test.id] : 0;
                                return (
                                    <div key={test.id} className="p-3 rounded-lg border border-white/5 bg-white/[0.02] flex items-start gap-3">
                                        <div className="mt-1 bg-blue-500/20 text-blue-400 text-[10px] font-bold px-1.5 py-0.5 rounded border border-blue-500/30">
                                            {score}
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-white/90">{test.name}</p>
                                            <p className="text-xs text-blue-300/80 mt-1">
                                                {test.scores[score]}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Resumo da Pontuação e Interpretação */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                        <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-600/20 to-indigo-600/20 border border-blue-500/30">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-blue-500 rounded-lg">
                                    <Star className="h-5 w-5 text-white" />
                                </div>
                                <h5 className="text-lg font-bold text-white">Pontuação Total</h5>
                            </div>
                            <p className="text-5xl font-black text-white mb-2">{evaluation.total_score}<span className="text-2xl text-white/40">/21</span></p>
                            <p className="text-sm text-blue-200/60 leading-relaxed">Soma integral de todas as habilidades psicomotoras avaliadas neste período.</p>
                        </div>

                        <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-indigo-500 rounded-lg">
                                    <Info className="h-5 w-5 text-white" />
                                </div>
                                <h5 className="text-lg font-bold text-white">Interpretação Clínica</h5>
                            </div>
                            <div className="space-y-3">
                                <p className="text-2xl font-bold text-blue-400 tracking-tight">
                                    {evaluation.result_interpretation}
                                </p>
                                <div className="h-px bg-white/10 w-full" />
                                <p className="text-sm text-white/70 italic leading-relaxed">
                                    {evaluation.global_interpretation || "O aluno demonstra progresso dentro dos critérios estabelecidos para sua faixa etária e condição física."}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default EvaluationDetailsModal;
